import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PROPERTY_TYPES, INCOME_OPTIONS, COSIGNER_OPTIONS } from "@/lib/options";

const initial = {
  rent: "", city: "", county: "", zip_code: "", bedrooms: "", bathrooms: "",
  property_type: "", min_credit_score: "", income_requirement: "", income_custom: "",
  reserves_required: "No", reserves_months: "", cosigners: "",
  background_check: "", pets_allowed: "", application_fee: "",
  listing_url: "", mls_number: "",
};

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-sm font-medium text-ink">{label}</Label>
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
    <div className="mt-2">{children}</div>
  </div>
);

const inputCls = "rounded-none border-slate-200 bg-white py-6 focus-visible:ring-navy";

const Toggle = ({ value, onChange, options, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`border px-5 py-2.5 text-sm transition-colors ${
          value === o ? "border-navy bg-cardinal-light text-ink" : "border-slate-200 text-slate-600 hover:border-slate-400"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export default function CriteriaForm({ onSubmitted }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rent || !form.city || !form.county || !form.property_type || !form.income_requirement || !form.cosigners) {
      toast.error("Please complete rent, city, county, property type, income requirement, and cosigner policy.");
      return;
    }
    setSubmitting(true);
    try {
      const num = (v) => (v === "" ? null : Number(v));
      await api.post("/criteria", {
        rent: Number(form.rent),
        city: form.city, county: form.county, zip_code: form.zip_code,
        bedrooms: num(form.bedrooms), bathrooms: num(form.bathrooms),
        property_type: form.property_type,
        min_credit_score: num(form.min_credit_score),
        income_requirement: form.income_requirement,
        income_custom: form.income_requirement === "Custom" ? num(form.income_custom) : null,
        reserves_required: form.reserves_required === "Yes",
        reserves_months: num(form.reserves_months),
        cosigners: form.cosigners,
        background_check: form.background_check === "" ? null : form.background_check === "Yes",
        pets_allowed: form.pets_allowed === "" ? null : form.pets_allowed === "Yes",
        application_fee: num(form.application_fee),
        listing_url: form.listing_url, mls_number: form.mls_number,
      });
      setDone(true);
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="criteria-success" className="border border-slate-200 bg-white p-10 text-center md:p-14">
        <div className="mx-auto flex h-14 w-14 items-center justify-center border border-navy">
          <Check className="h-7 w-7 text-navy" />
        </div>
        <h3 className="mt-6 font-serif text-2xl font-medium text-ink">Thank you — submitted for verification.</h3>
        <p className="mx-auto mt-3 max-w-lg text-slate-600">
          Every listing is reviewed by our research team before it is counted. Nothing is
          published individually — your contribution becomes part of anonymous, aggregate
          statistics only.
        </p>
        <Button variant="outline" className="mt-6 rounded-none border-navy" onClick={() => { setForm(initial); setDone(false); }}>
          Contribute another listing
        </Button>
      </div>
    );
  }

  return (
    <form data-testid="criteria-form" onSubmit={submit} className="space-y-10 border border-slate-200 bg-white p-6 md:p-10">
      {/* Property */}
      <div>
        <h3 className="font-serif text-xl font-medium text-ink">Property Information</h3>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Monthly Rent ($)"><Input data-testid="rent-input" type="number" value={form.rent} onChange={(e) => set("rent", e.target.value)} className={inputCls} /></Field>
          <Field label="Property Type">
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger data-testid="property-type-select" className="rounded-none border-slate-200 py-6"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="City"><Input data-testid="city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field>
          <Field label="County"><Input data-testid="county-input" value={form.county} onChange={(e) => set("county", e.target.value)} placeholder="e.g., Orange County" className={inputCls} /></Field>
          <Field label="ZIP Code"><Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Bedrooms"><Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inputCls} /></Field>
            <Field label="Bathrooms"><Input type="number" step="0.5" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className={inputCls} /></Field>
          </div>
        </div>
      </div>

      {/* Qualification */}
      <div className="border-t border-slate-200 pt-8">
        <h3 className="font-serif text-xl font-medium text-ink">Qualification Criteria</h3>
        <div className="mt-5 space-y-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Minimum Credit Score"><Input data-testid="credit-input" type="number" value={form.min_credit_score} onChange={(e) => set("min_credit_score", e.target.value)} className={inputCls} /></Field>
            <Field label="Income Requirement">
              <Select value={form.income_requirement} onValueChange={(v) => set("income_requirement", v)}>
                <SelectTrigger data-testid="income-select" className="rounded-none border-slate-200 py-6"><SelectValue placeholder="Select multiple of rent" /></SelectTrigger>
                <SelectContent>{INCOME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {form.income_requirement === "Custom" && (
            <Field label="Custom income multiple (× rent)"><Input type="number" step="0.1" value={form.income_custom} onChange={(e) => set("income_custom", e.target.value)} className={inputCls} /></Field>
          )}
          <Field label="Reserves Required?"><Toggle testid="reserves-toggle" value={form.reserves_required} onChange={(v) => set("reserves_required", v)} options={["Yes", "No"]} /></Field>
          {form.reserves_required === "Yes" && (
            <Field label="How many months of reserves?"><Input type="number" value={form.reserves_months} onChange={(e) => set("reserves_months", e.target.value)} className={inputCls} /></Field>
          )}
          <Field label="Cosigners Allowed?"><Toggle testid="cosigner-toggle" value={form.cosigners} onChange={(v) => set("cosigners", v)} options={COSIGNER_OPTIONS} /></Field>
        </div>
      </div>

      {/* Additional */}
      <div className="border-t border-slate-200 pt-8">
        <h3 className="font-serif text-xl font-medium text-ink">Additional Requirements</h3>
        <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <Field label="Background Check"><Toggle value={form.background_check} onChange={(v) => set("background_check", v)} options={["Yes", "No"]} /></Field>
          <Field label="Pets Allowed"><Toggle value={form.pets_allowed} onChange={(v) => set("pets_allowed", v)} options={["Yes", "No"]} /></Field>
          <Field label="Application Fee ($)"><Input type="number" value={form.application_fee} onChange={(e) => set("application_fee", e.target.value)} className={inputCls} /></Field>
        </div>
      </div>

      {/* Source */}
      <div className="border-t border-slate-200 pt-8">
        <h3 className="font-serif text-xl font-medium text-ink">Source (helps our team verify)</h3>
        <p className="mt-1 text-xs text-slate-400">Optional. Used only for verification — never published.</p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Listing URL"><Input data-testid="listing-url-input" value={form.listing_url} onChange={(e) => set("listing_url", e.target.value)} className={inputCls} /></Field>
          <Field label="MLS Number (if available)"><Input value={form.mls_number} onChange={(e) => set("mls_number", e.target.value)} className={inputCls} /></Field>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 pt-8">
        <p className="max-w-md text-xs text-slate-400">
          We document advertised criteria only. We never name or evaluate individual
          landlords, agents, or brokerages.
        </p>
        <Button type="submit" data-testid="submit-criteria-button" disabled={submitting}
          className="rounded-none border border-navy bg-transparent px-10 py-6 text-base font-medium text-navy transition-colors hover:bg-navy hover:text-white">
          {submitting ? "Submitting…" : "Submit Listing"}
        </Button>
      </div>
    </form>
  );
}
